import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FileCheck, FileSearch, ShieldCheck, Clock } from "lucide-react";

const STEPS = [
  { key: "uploaded", label: "Uploaded", icon: Clock, hint: "Details and photos received" },
  { key: "legal_review", label: "Legal Review", icon: FileSearch, hint: "RERA, Khata & OC under review" },
  { key: "verified", label: "Verified", icon: ShieldCheck, hint: "Live on NimmaSwathu" },
];

export const DocumentStatusTracker = ({ property, onUploadRequest }) => {
  const { user } = useAuth();
  const [docsCount, setDocsCount] = useState(0);

  useEffect(() => {
    if (!property?.id) return;
    api.get(`/properties/${property.id}/documents`).then(({ data }) => setDocsCount(data.documents?.length || 0)).catch(() => {});
  }, [property]);

  if (!property || property.verification_tier !== "document_verified") return null;
  // Only show to owner or admin
  const canSee = user && (user.id === property.owner_id || user.role === "admin");
  if (!canSee) return null;

  // Determine step state
  const uploaded = true;
  const inReview = property.status === "pending" && docsCount > 0;
  const verified = property.status === "approved";
  const current = verified ? "verified" : inReview ? "legal_review" : "uploaded";
  const indexes = { uploaded: 0, legal_review: 1, verified: 2 };
  const activeIdx = indexes[current];

  return (
    <div className="p-5 rounded-2xl border border-[rgba(212,163,89,0.4)] bg-[rgba(212,163,89,0.06)]" data-testid="document-status-tracker">
      <div className="flex items-center gap-2 mb-4">
        <FileCheck className="w-4 h-4 text-[var(--gold)]" />
        <span className="text-xs uppercase tracking-wider font-semibold text-[var(--teak)]">Document Verification · ₹1,999 tier</span>
      </div>

      <div className="relative">
        {/* Rail */}
        <div className="absolute top-5 left-5 right-5 h-0.5 bg-[rgba(212,163,89,0.25)]" />
        <div
          className="absolute top-5 left-5 h-0.5 bg-[var(--gold)] transition-all duration-700"
          style={{ width: `${(activeIdx / (STEPS.length - 1)) * 100}%`, maxWidth: "calc(100% - 2.5rem)" }}
        />
        <div className="relative grid grid-cols-3 gap-2">
          {STEPS.map((s, i) => {
            const done = i < activeIdx;
            const active = i === activeIdx;
            const Icon = s.icon;
            return (
              <div key={s.key} className="flex flex-col items-center text-center" data-testid={`step-${s.key}${active ? "-active" : ""}`}>
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                    done
                      ? "bg-[var(--gold)] border-[var(--gold)] text-white"
                      : active
                      ? "bg-white border-[var(--gold)] text-[var(--gold)]"
                      : "bg-white border-[var(--line)] text-[var(--ink-muted)]"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                </div>
                <div className={`font-serif text-sm mt-2 ${active || done ? "text-[var(--teak)]" : "text-[var(--ink-muted)]"}`}>
                  {s.label}
                </div>
                <div className="text-[10px] text-[var(--ink-muted)] leading-tight mt-0.5 px-1">{s.hint}</div>
              </div>
            );
          })}
        </div>
      </div>

      {current !== "verified" && (
        <div className="mt-4 flex flex-col items-center gap-2">
          <p className="text-[11px] text-[var(--ink-muted)] text-center">
            {current === "uploaded"
              ? "Upload RERA, Khata and OC under the Document Vault to kick off the legal review."
              : "Our legal team is vetting your documents — this usually finishes within 24 hours."}
          </p>
          {current === "uploaded" && (
            <button
              type="button"
              onClick={() => onUploadRequest?.()}
              className="btn-primary text-xs py-2 px-4"
              data-testid="tracker-upload-now-btn"
            >
              <FileCheck className="w-3.5 h-3.5" /> Upload documents now
            </button>
          )}
        </div>
      )}
    </div>
  );
};
